import { usePlans } from '@/hooks/usePlans';
import { useClickref } from '@/hooks/useClickref';
import { PLAN_TYPES, type PlanType } from '@/config/api';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import './HomeFeaturedCard.scss';
import { formatPrice } from '@/utils/formatPrice';

export interface HomeFeaturedCardProps {
    planType?: PlanType;
    planId?: string;
    showAnnonce?: boolean;
    position?: number;
}

function slugify(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

export function HomeFeaturedCard({ planType = PLAN_TYPES.MOBILE, planId, showAnnonce = true, position = 1 }: HomeFeaturedCardProps) {
    const { plans } = usePlans(planType);
    const { getClickrefUrl, handleClickrefLink } = useClickref();
    const plan = planId
        ? plans.find(p => p.id === planId) ?? null
        : null;

    if (!plan) {
        return null;
    }

    const { operator, offer, name } = plan;
    const price = offer.price.price;
    const crossedPrice = offer.price.crossed_price;
    const rawUrl = offer.url_lemon;
    const ctaUrl = getClickrefUrl(rawUrl);

    return (
        <a
            href={ctaUrl}
            onClick={(e) => handleClickrefLink(e, rawUrl)}
            target="_blank"
            rel="noopener nofollow noreferrer"
            className="no-underline flex"
            data-gtm-offer-type="comparator_link"
            data-gtm-bloc-type="featured"
            data-gtm-offer-url={ctaUrl}
            data-gtm-seller={operator.slug}
            data-gtm-product-name={slugify(name)}
            data-gtm-position-marchand={position}
            data-gtm-product-category={planType}
            data-gtm-zone="comparator_featured"
            data-gtm-position={position}
        >
            <div className="home-featured-card">
                <div className="home-featured-card__header">
                    <span className="home-featured-card__title">{decodeHtmlEntities(name)}</span>
                    <div className="home-featured-card__logo-wrap">
                        <img
                            src={operator.logo}
                            alt={operator.name}
                            className="home-featured-card__logo"
                            width={50}
                            height={50}
                        />
                        {showAnnonce && <span className="home-featured-card__annonce">Annonce</span>}
                    </div>
                </div>
                <hr className="home-featured-card__separator" />
                <div className="home-featured-card__price">
                    <span className="home-featured-card__price-value">{formatPrice(price)}&euro;</span>
                    <span className="home-featured-card__price-period">/mois</span>
                </div>
                {crossedPrice && (
                    <span className="home-featured-card__price-crossed">
                        {formatPrice(crossedPrice)}&euro;/mois
                    </span>
                )}
                <span className="home-featured-card__cta">
                    Voir l&rsquo;offre
                </span>
            </div>
        </a>
    );
}

export default HomeFeaturedCard;
